import { useEffect, useMemo, useState, useRef } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import Textarea from '../components/Textarea'
import Select from '../components/Select'
import {
  EXAM_SECTIONS,
  QUESTION_TYPE_OPTIONS,
  CHOICE_OPTIONS,
} from '../utils/constants'
import {
  Stethoscope,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Trash2,
  Plus,
  Clock,
  Save,
  FileText,
  X,
  Download,
  Upload,
  FileSpreadsheet,
} from 'lucide-react'
import { saveToLocalStorage, loadFromLocalStorage, clearLocalStorage, AUTOSAVE_INTERVAL } from '../services/autosave'
import { saveDraft, loadDraft, deleteDraft } from '../services/draftSaving'
import { saveExaminationTransaction } from '../services/transactions'
import { findExistingClient } from '../services/clientLookup'
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus'
import { hasReachedDemoLimit, saveDemoEntry, initDemoSession } from '../services/demoStorage'
import { usePreventNavigation } from '../hooks/usePreventNavigation'
import DemoUpgradePrompt from '../components/DemoUpgradePrompt'
import ExaminationTemplateSelector from '../components/ExaminationTemplateSelector'
import DraftManager from '../components/DraftManager'
import HabitManager from '../components/HabitManager'
import ClientSearch from '../components/ClientSearch'
import QuestionnaireTemplateManager from '../components/QuestionnaireTemplateManager'
import WellnessHabitsSection from '../components/WellnessHabitsSection'
import ExaminationImportExport from '../components/ExaminationImportExport'
import ExaminationPdfEditor from '../components/ExaminationPdfEditor'
import { exportExaminationFormToCSV } from '../services/examinationImportExport'

const schema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  dateOfBirth: z.string().min(1, 'Date of birth is required'),
  gender: z.string().min(1, 'Gender is required'),
  phone: z.string().optional(),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  currentMedications: z.string().optional(),
  allergies: z.string().optional(),
  pastSurgeries: z.string().optional(),
  familyHistory: z.string().optional(),
  chronicConditions: z.string().optional(),
  diet: z.array(z.object({
    id: z.string(),
    text: z.string(),
    tagged: z.boolean(),
  })).optional(),
  exercise: z.array(z.object({
    id: z.string(),
    text: z.string(),
    tagged: z.boolean(),
  })).optional(),
  sleep: z.array(z.object({
    id: z.string(),
    text: z.string(),
    tagged: z.boolean(),
  })).optional(),
  customHabits: z.record(z.string(), z.array(z.object({
    id: z.string(),
    text: z.string(),
    tagged: z.boolean(),
  }))).optional(),
  stressLevel: z.string().optional(),
  alcohol: z.string().optional(),
  smoking: z.string().optional(),
  primaryGoals: z.string().min(1, 'Primary goals are required'),
  expectations: z.string().optional(),
  previousTreatments: z.string().optional(),
  bloodPressure: z.string().min(1, 'Blood pressure is required'),
  heartRate: z.string().min(1, 'Heart rate is required'),
  respiratoryRate: z.string().optional(),
  temperature: z.string().optional(),
  oxygenSaturation: z.string().optional(),
  weight: z.string().optional(),
  height: z.string().optional(),
  bmi: z.string().optional(),
  clientConsent: z.boolean().refine((val) => val === true, {
    message: 'Client consent is required to save client information',
  }),
  consentDate: z.string().min(1, 'Consent date is required'),
  consentWitness: z.string().optional(),
})

const createQuestion = () => ({
  id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
  prompt: '',
  type: 'choice',
  answer: '',
})

const Examination = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const organizationId = useOrganizationId()
  const { isDemo } = useSubscriptionStatus()
  const [currentStep, setCurrentStep] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [customQuestions, setCustomQuestions] = useState([createQuestion()])
  const [lastSaved, setLastSaved] = useState(null)
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle')
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [showTemplates, setShowTemplates] = useState(false)
  const [showDraftManager, setShowDraftManager] = useState(false)
  const examIdRef = useRef(`exam_${Date.now()}`)
  const autoSaveIntervalRef = useRef(null)
  const [userId, setUserId] = useState(null)
  const draftSaveTimeoutRef = useRef(null)
  const hasUserInputRef = useRef(false)
  const [dataRestored, setDataRestored] = useState(false)
  const [existingClient, setExistingClient] = useState(null)
  const [checkingClient, setCheckingClient] = useState(false)
  const [skippedSections, setSkippedSections] = useState([])
  const [reachedDemoLimit, setReachedDemoLimit] = useState(false)
  const [selectedClientForAutoFill, setSelectedClientForAutoFill] = useState(null)
  const [showCsvTransfer, setShowCsvTransfer] = useState(false)
  const [backupStatus, setBackupStatus] = useState('')
  const [pdfEditorOpen, setPdfEditorOpen] = useState(false)
  const [pdfEditorBlank, setPdfEditorBlank] = useState(false)

  // Check demo limit on mount
  useEffect(() => {
    if (isDemo) {
      initDemoSession()
      if (hasReachedDemoLimit('examination')) {
        setReachedDemoLimit(true)
      }
    }
  }, [isDemo])

  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: '',
      phone: '',
      email: '',
      currentMedications: '',
      allergies: '',
      pastSurgeries: '',
      familyHistory: '',
      chronicConditions: '',
      diet: [],
      exercise: [],
      sleep: [],
      customHabits: {},
      stressLevel: '',
      alcohol: '',
      smoking: '',
      primaryGoals: '',
      expectations: '',
      previousTreatments: '',
      bloodPressure: '',
      heartRate: '',
      respiratoryRate: '',
      temperature: '',
      oxygenSaturation: '',
      weight: '',
      height: '',
      bmi: '',
      clientConsent: false,
      consentDate: new Date().toISOString().split('T')[0],
      consentWitness: '',
    },
    mode: 'onBlur',
  })

  const {
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
  } = methods

  // Prevent navigation if form has unsaved changes
  usePreventNavigation(isDirty && !success, 'You have unsaved examination data. Are you sure you want to leave?')

  const weight = watch('weight')
  const height = watch('height')

  // Calculate total steps excluding skipped sections
  const availableSections = EXAM_SECTIONS.filter((section) => !skippedSections.includes(section.id))
  const totalSteps = availableSections.length

  useEffect(() => {
    if (!weight || !height) {
      setValue('bmi', '')
      return
    }
    const kg = parseFloat(weight)
    const cm = parseFloat(height)
    if (kg > 0 && cm > 0) {
      const bmi = kg / Math.pow(cm / 100, 2)
      setValue('bmi', bmi.toFixed(1))
    }
  }, [weight, height, setValue])

  // Get user ID on mount
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      setUserId(user?.id)
    }
    getUser()
  }, [])

  // Helper to convert old string format habits to new array format
  const normalizeHabits = (value) => {
    if (!value) return []
    if (Array.isArray(value)) return value
    if (typeof value === 'string' && value.trim()) {
      // Convert old string format to array format
      return value.split('\n').filter(line => line.trim()).map((text, idx) => ({
        id: `habit_${Date.now()}_${idx}`,
        text: text.trim(),
        tagged: false,
      }))
    }
    return []
  }

  // Load autosaved data on mount (localStorage + server draft)
  // Auto-restore on mobile/page reload without confirmation
  useEffect(() => {
    const loadAutosaved = async () => {
      // Try localStorage first (faster)
      const localAutosaved = loadFromLocalStorage(examIdRef.current)
      if (localAutosaved && (localAutosaved.formData || localAutosaved.customQuestions)) {
        // Normalize habits from old format to new format
        const formData = { ...localAutosaved.formData }
        if (formData.diet) formData.diet = normalizeHabits(formData.diet)
        if (formData.exercise) formData.exercise = normalizeHabits(formData.exercise)
        if (formData.sleep) formData.sleep = normalizeHabits(formData.sleep)
        
        // Auto-restore for mobile/page reload scenarios
        methods.reset(formData)
        setCustomQuestions(localAutosaved.customQuestions || [createQuestion()])
        setCurrentStep(localAutosaved.currentStep || 0)
        
        // Show restoration indicator
        setDataRestored(true)
        setTimeout(() => setDataRestored(false), 3000)
        return
      }

      // Try server draft if no localStorage
      if (organizationId && userId) {
        const serverDraft = await loadDraft(organizationId, userId, 'examination', examIdRef.current)
        if (serverDraft && serverDraft.draft_data) {
          // Normalize habits from old format to new format
          const formData = { ...serverDraft.draft_data.formData }
          if (formData.diet) formData.diet = normalizeHabits(formData.diet)
          if (formData.exercise) formData.exercise = normalizeHabits(formData.exercise)
          if (formData.sleep) formData.sleep = normalizeHabits(formData.sleep)
          
          // Auto-restore from server draft
          methods.reset(formData)
          setCustomQuestions(serverDraft.draft_data.customQuestions || [createQuestion()])
          setCurrentStep(serverDraft.draft_data.currentStep || 0)
          
          // Show restoration indicator
          setDataRestored(true)
          setTimeout(() => setDataRestored(false), 3000)
        }
      }
    }

    if (organizationId && userId) {
      loadAutosaved()
    }
  }, [organizationId, userId])

  // Handler to load a draft from DraftManager
  const handleLoadDraft = (draft) => {
    if (draft.formData) {
      // Normalize habits from old format to new format
      const formData = { ...draft.formData }
      if (formData.diet) formData.diet = normalizeHabits(formData.diet)
      if (formData.exercise) formData.exercise = normalizeHabits(formData.exercise)
      if (formData.sleep) formData.sleep = normalizeHabits(formData.sleep)
      methods.reset(formData)
    }
    if (draft.customQuestions) {
      setCustomQuestions(draft.customQuestions)
    }
    if (draft.currentStep !== undefined) {
      setCurrentStep(draft.currentStep)
    }
    setDataRestored(true)
    setTimeout(() => setDataRestored(false), 3000)
    setShowDraftManager(false)
  }

  // Autosave effect (localStorage + server) - only saves on input changes
  useEffect(() => {
    if (!organizationId || !userId) return

    const autoSave = async () => {
      // Only save if user has made input
      if (!hasUserInputRef.current) return

      const formValues = methods.getValues()
      const dataToSave = {
        formData: formValues,
        customQuestions,
        currentStep,
      }

      // Check if content has changed before saving
      const existingLocalData = loadFromLocalStorage(examIdRef.current)
      const hasContentChanged = !existingLocalData || 
        JSON.stringify(existingLocalData.formData) !== JSON.stringify(dataToSave.formData) ||
        JSON.stringify(existingLocalData.customQuestions) !== JSON.stringify(dataToSave.customQuestions) ||
        existingLocalData.currentStep !== dataToSave.currentStep

      // If content hasn't changed, skip saving (but still update timestamp if needed)
      if (!hasContentChanged) {
        // Content is identical - just update timestamp silently
        saveToLocalStorage(examIdRef.current, dataToSave)
        return
      }

      setAutoSaveStatus('saving')
      
      // Save to localStorage (fast, always works)
      // This will update the existing draft if content is identical
      const localSaved = saveToLocalStorage(examIdRef.current, dataToSave)
      
      // Save to server (slower, but persistent)
      // This will also check for identical content and update timestamp only
      if (organizationId && userId) {
        try {
          await saveDraft(organizationId, userId, 'examination', examIdRef.current, dataToSave)
        } catch (error) {
          console.error('Server draft save failed:', error)
          // Don't fail the whole autosave if server save fails
        }
      }
      
      if (localSaved) {
        setAutoSaveStatus('saved')
        setLastSaved(new Date())
        setTimeout(() => {
          setAutoSaveStatus('idle')
        }, 2000)
      } else {
        setAutoSaveStatus('error')
      }
    }

    // Debounced auto-save on form changes - only triggers after user input
    const watchSubscription = methods.watch(() => {
      // Mark that user has made input
      hasUserInputRef.current = true
      
      if (draftSaveTimeoutRef.current) {
        clearTimeout(draftSaveTimeoutRef.current)
      }
      draftSaveTimeoutRef.current = setTimeout(autoSave, 30000) // 30 seconds after last change
    })

    // Autosave every 30 seconds (only if user has made input)
    autoSaveIntervalRef.current = setInterval(autoSave, AUTOSAVE_INTERVAL)

    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current)
      }
      if (draftSaveTimeoutRef.current) {
        clearTimeout(draftSaveTimeoutRef.current)
      }
      watchSubscription.unsubscribe()
    }
  }, [methods, customQuestions, currentStep, organizationId, userId])

  const sanitizedQuestions = useMemo(
    () =>
      customQuestions
        .filter((q) => q.prompt.trim())
        .map((q) => ({
          prompt: q.prompt.trim(),
          type: q.type,
          answer: q.answer,
        })),
    [customQuestions]
  )

  const onSubmit = async (values) => {
    setSubmitting(true)
    setError('')
    try {
      if (!organizationId) throw new Error('No organization found')

      // Check demo limit
      if (isDemo && hasReachedDemoLimit('examination')) {
        setReachedDemoLimit(true)
        setSubmitting(false)
        return
      }

      // Check client consent before saving
      if (!values.clientConsent) {
        throw new Error('Client consent is required to save client information. Please obtain consent before proceeding.')
      }

      // Normalize vital signs - ensure required fields are strings, not null
      const vitals = {
        bloodPressure: values.bloodPressure || '',
        heartRate: values.heartRate || '',
        respiratoryRate: values.respiratoryRate || null,
        temperature: values.temperature || null,
        oxygenSaturation: values.oxygenSaturation || null,
        weight: values.weight || null,
        height: values.height || null,
        bmi: values.bmi || null,
      }

      // Client-side validation before submission
      if (!vitals.bloodPressure || vitals.bloodPressure.trim() === '') {
        setError('Blood pressure is required. Please fill in all required vital signs.')
        setSubmitting(false)
        // Scroll to vital signs section
        const vitalSection = document.querySelector('[data-section="vitals"]')
        if (vitalSection) {
          vitalSection.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
        return
      }

      if (!vitals.heartRate || vitals.heartRate.trim() === '') {
        setError('Heart rate is required. Please fill in all required vital signs.')
        setSubmitting(false)
        // Scroll to vital signs section
        const vitalSection = document.querySelector('[data-section="vitals"]')
        if (vitalSection) {
          vitalSection.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
        return
      }

      // For demo users, save to localStorage instead of database
      if (isDemo) {
        const demoEntry = {
          client: {
            first_name: values.firstName,
            last_name: values.lastName,
            date_of_birth: values.dateOfBirth,
            gender: values.gender,
            phone: values.phone || null,
            email: values.email || null,
          },
          vital_signs: vitals,
          medical_history: {
            current_medications: values.currentMedications || null,
            allergies: values.allergies || null,
            past_surgeries: values.pastSurgeries || null,
            family_history: values.familyHistory || null,
            chronic_conditions: values.chronicConditions || null,
            diet: values.diet || null,
            exercise: values.exercise || null,
            sleep: values.sleep || null,
            custom_habits: values.customHabits && Object.keys(values.customHabits).length > 0 ? values.customHabits : null,
            stress_level: values.stressLevel || null,
            alcohol: values.alcohol || null,
            smoking: values.smoking || null,
            primary_goals: values.primaryGoals,
            expectations: values.expectations || null,
            previous_treatments: values.previousTreatments || null,
          },
          custom_questions: sanitizedQuestions,
        }
        
        saveDemoEntry('examination', demoEntry)
        setSuccess(true)
        methods.reset()
        setCustomQuestions([createQuestion()])
        setCurrentStep(0)
        setSubmitting(false)
        return
      }

      // Check for existing client before creating new one
      const existingClient = await findExistingClient(
        organizationId,
        values.firstName,
        values.lastName,
        values.dateOfBirth
      )

      // Use transaction for atomic save (all or nothing)
      const result = await saveExaminationTransaction(organizationId, {
        client: {
          id: existingClient?.id || null, // Use existing client ID if found
          first_name: values.firstName,
          last_name: values.lastName,
          date_of_birth: values.dateOfBirth,
          gender: values.gender,
          phone: values.phone || null,
          email: values.email || null,
        },
        medicalHistory: {
          id: existingClient?.medical_history?.id || null, // Use existing history ID if found
          organization_id: organizationId,
          current_medications: values.currentMedications || null,
          allergies: values.allergies || null,
          past_surgeries: values.pastSurgeries || null,
          family_history: values.familyHistory || null,
          chronic_conditions: values.chronicConditions || null,
          diet: values.diet || null,
          exercise: values.exercise || null,
          sleep: values.sleep || null,
          custom_habits: values.customHabits && Object.keys(values.customHabits).length > 0 ? values.customHabits : null,
          stress_level: values.stressLevel || null,
          alcohol: values.alcohol || null,
          smoking: values.smoking || null,
          primary_goals: values.primaryGoals,
          expectations: values.expectations || null,
          previous_treatments: values.previousTreatments || null,
        },
        vitalSigns: vitals,
        customQuestions: sanitizedQuestions,
        examinationId: null,
      }, userId)

      if (!result.success) {
        throw new Error('Failed to save examination. All changes have been rolled back.')
      }

      // Clear autosave after successful save
      clearLocalStorage(examIdRef.current)
      // Delete server-side draft
      if (organizationId && userId) {
        await deleteDraft(organizationId, userId, 'examination', examIdRef.current)
      }
      
      // Get the client ID from the result
      const clientResult = result.results?.find((r) => r.first_name || r.last_name)
      const clientId = clientResult?.id || existingClient?.id
      
      setSuccess(true)
      
      // Option to create follow-up reminder
      const createReminder = window.confirm('Would you like to set a follow-up reminder for this client?')
      if (createReminder && clientId) {
        // Navigate to client details with reminder tab open
        navigate(`/clients/${clientId}?tab=reminders`)
      } else {
        // Reset form after a short delay to show success message
        setTimeout(() => {
          methods.reset()
          setCustomQuestions([createQuestion()])
          setCurrentStep(0)
        }, 2000)
      }
    } catch (err) {
      console.error(err)
      setError(
        err.message ||
          'Something went wrong while saving the examination. Please ensure the examinations table exists and you have permission to write to it.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  const goNext = async () => {
    const section = availableSections[currentStep]
    const fieldNames = section.fields?.map((f) => f.name) || []
    const result = fieldNames.length
      ? await methods.trigger(fieldNames, { shouldFocus: true })
      : true
    if (!result) return
    if (currentStep < totalSteps - 1) setCurrentStep((prev) => prev + 1)
  }

  const goBack = () => {
    if (currentStep > 0) setCurrentStep((prev) => prev - 1)
  }

  const updateQuestion = (id, key, value) => {
    setCustomQuestions((prev) =>
      prev.map((question) =>
        question.id === id
          ? {
              ...question,
              [key]: value,
              ...(key === 'type' ? { answer: '' } : {}),
            }
          : question
      )
    )
  }

  const removeQuestion = (id) => {
    setCustomQuestions((prev) => (prev.length === 1 ? prev : prev.filter((q) => q.id !== id)))
  }

  const addQuestion = () => {
    setCustomQuestions((prev) => [...prev, createQuestion()])
  }

  const currentSection = availableSections[currentStep]

  return (
    <div className="section-container">
      {/* Upgrade Prompt for Demo Users - Show proactively */}
      {isDemo && (
        <DemoUpgradePrompt featureName="examinations" limitReached={reachedDemoLimit} />
      )}
      
      {/* Show message if limit reached */}
      {isDemo && reachedDemoLimit && (
        <Card variant="soft" className="p-6 text-center mb-6">
          <p className="text-gray-600">You've reached the demo limit. Start your free trial to create unlimited examinations.</p>
        </Card>
      )}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-lg">
              <Stethoscope size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('examination.newExamination')}</h1>
              <p className="text-gray-600 dark:text-gray-300">
                {t('examination.newExaminationDescription')}
              </p>
            </div>
          </div>
          {/* Autosave Status */}
          <div className="flex items-center gap-2 text-xs">
            {/* Data Restored Indicator */}
            {dataRestored && (
              <motion.span
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="text-green-600 dark:text-green-400 flex items-center gap-1 bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded"
              >
                <CheckCircle2 size={14} />
                {t('examination.dataRestored')}
              </motion.span>
            )}
            {autoSaveStatus === 'saving' && (
              <span className="text-gray-500 flex items-center gap-1">
                <Clock size={14} className="animate-spin" />
                {t('examination.saving')}
              </span>
            )}
            {autoSaveStatus === 'saved' && (
              <span className="text-green-600 flex items-center gap-1">
                <CheckCircle2 size={14} />
                {t('examination.saved')}
              </span>
            )}
            {autoSaveStatus === 'error' && (
              <span className="text-red-600 flex items-center gap-1">
                <Save size={14} />
                {t('examination.saveFailed')}
              </span>
            )}
            {lastSaved && autoSaveStatus === 'idle' && (
              <span className="text-gray-400">
                {t('examination.lastSaved', { time: new Date(lastSaved).toLocaleTimeString() })}
              </span>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowDraftManager(true)}
              className="ml-2"
            >
              <FileText size={14} className="mr-1" />
              {t('examination.viewDrafts')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setPdfEditorBlank(false)
                setPdfEditorOpen(true)
              }}
              className="flex items-center gap-1"
              title="Prepare and download examination PDF"
            >
              <Download size={14} />
              Print form
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setPdfEditorBlank(true)
                setPdfEditorOpen(true)
              }}
              className="flex items-center gap-1"
              title="Prepare a blank paper chart PDF"
            >
              <FileText size={14} />
              Blank chart
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                exportExaminationFormToCSV(methods.getValues(), customQuestions)
                setBackupStatus('CSV downloaded')
                setTimeout(() => setBackupStatus(''), 2500)
              }}
              className="flex items-center gap-1"
              title="Download CSV of current form fields"
            >
              <FileSpreadsheet size={14} />
              CSV
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowCsvTransfer((v) => !v)}
              className="flex items-center gap-1"
              title="Import or export examination CSV files"
            >
              <Upload size={14} />
              Transfer
            </Button>
          </div>
          {backupStatus && (
            <p className="text-xs text-gray-500 mt-2 w-full sm:w-auto sm:absolute sm:right-0 sm:top-full">
              {backupStatus}
            </p>
          )}
        </div>
      </motion.div>

      {showCsvTransfer && (
        <div className="mb-6">
          <ExaminationImportExport onClose={() => setShowCsvTransfer(false)} />
        </div>
      )}

      <ExaminationPdfEditor
        open={pdfEditorOpen}
        onClose={() => setPdfEditorOpen(false)}
        blankMode={pdfEditorBlank}
        formValues={pdfEditorBlank ? null : methods.getValues()}
        customQuestions={pdfEditorBlank ? [] : customQuestions}
        title={pdfEditorBlank ? 'Prepare blank chart PDF' : 'Prepare examination PDF'}
      />

      {/* Draft Manager Modal */}
      <AnimatePresence>
        {showDraftManager && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDraftManager(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col"
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t('examination.savedDrafts')}</h2>
                <button
                  onClick={() => setShowDraftManager(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                <DraftManager
                  organizationId={organizationId}
                  userId={userId}
                  onLoadDraft={handleLoadDraft}
                  onClose={() => setShowDraftManager(false)}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Draft Manager Modal */}
      <AnimatePresence>
        {showDraftManager && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDraftManager(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col"
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t('examination.savedDrafts')}</h2>
                <button
                  onClick={() => setShowDraftManager(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                <DraftManager
                  organizationId={organizationId}
                  userId={userId}
                  onLoadDraft={handleLoadDraft}
                  onClose={() => setShowDraftManager(false)}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Template Selector */}
      <AnimatePresence>
        {showTemplates && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6"
          >
            <Card variant="soft" className="p-6">
              <ExaminationTemplateSelector
                onSelectTemplate={(template) => {
                  setSelectedTemplate(template)
                  setShowTemplates(false)
                  // TODO: Load template data into form
                }}
                selectedTemplateId={selectedTemplate?.id}
              />
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Template Button */}
      <div className="mb-4 flex justify-end">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowTemplates(!showTemplates)}
          className="flex items-center gap-2"
        >
          <FileText size={16} />
          {selectedTemplate ? t('examination.templateSelected', { name: selectedTemplate.name }) : t('examination.useTemplate')}
        </Button>
      </div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <Card variant="glass" className="p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-gray-700">
              {t('examination.step', { current: currentStep + 1, total: totalSteps })}:{' '}
              <span className="text-primary-600">{t(`examination.sections.${currentSection.id}`) || currentSection.title}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {availableSections.map((section, index) => {
              const completed = index < currentStep
              const active = index === currentStep
              return (
                <div key={section.id} className="flex-1">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      completed
                        ? 'bg-gradient-to-r from-primary-500 to-secondary-500'
                        : active
                        ? 'bg-primary-300'
                        : 'bg-gray-200'
                    }`}
                  />
                  <p
                    className={`mt-2 text-xs font-medium text-center ${
                      active ? 'text-primary-600' : 'text-gray-500'
                    }`}
                  >
                    {t(`examination.sections.${section.id}`) || section.title}
                  </p>
                </div>
              )
            })}
          </div>
        </Card>
      </motion.div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm"
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm flex items-center gap-2"
          >
            <CheckCircle2 size={18} />
            {t('examination.examinationSaved')}
          </motion.div>
        )}
      </AnimatePresence>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSection.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card variant="soft" className="p-6" data-section={currentSection.id}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {t(`examination.sections.${currentSection.id}`) || currentSection.title}
                    </h2>
                    {currentSection.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {t(`examination.${currentSection.id}Description`) || currentSection.description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Client Search for Personal Details Section */}
                {currentSection.id === 'personal' && (
                  <div className="mb-6">
                    <ClientSearch
                      onSelectClient={(client) => {
                        setSelectedClientForAutoFill(client)
                        if (client) {
                          // Auto-fill personal details
                          methods.setValue('firstName', client.first_name || '', { shouldDirty: true })
                          methods.setValue('lastName', client.last_name || '', { shouldDirty: true })
                          methods.setValue('dateOfBirth', client.date_of_birth || '', { shouldDirty: true })
                          methods.setValue('gender', client.gender || '', { shouldDirty: true })
                          methods.setValue('phone', client.phone || '', { shouldDirty: true })
                          methods.setValue('email', client.email || '', { shouldDirty: true })
                          hasUserInputRef.current = true
                        }
                      }}
                      selectedClientId={selectedClientForAutoFill?.id}
                    />
                  </div>
                )}

                {currentSection.id !== 'questionnaire' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Special handling for lifestyle section - use WellnessHabitsSection */}
                    {currentSection.id === 'lifestyle' ? (
                      <>
                        <div className="md:col-span-2">
                          <WellnessHabitsSection
                          diet={methods.watch('diet') || []}
                          exercise={methods.watch('exercise') || []}
                          sleep={methods.watch('sleep') || []}
                          customHabits={methods.watch('customHabits') || {}}
                          onDietChange={(habits) => {
                            methods.setValue('diet', habits, { shouldDirty: true })
                            hasUserInputRef.current = true
                          }}
                          onExerciseChange={(habits) => {
                            methods.setValue('exercise', habits, { shouldDirty: true })
                            hasUserInputRef.current = true
                          }}
                          onSleepChange={(habits) => {
                            methods.setValue('sleep', habits, { shouldDirty: true })
                            hasUserInputRef.current = true
                          }}
                          onCustomHabitChange={(categoryId, habits) => {
                            const currentCustomHabits = methods.watch('customHabits') || {}
                            methods.setValue('customHabits', {
                              ...currentCustomHabits,
                              [categoryId]: habits,
                            }, { shouldDirty: true })
                            hasUserInputRef.current = true
                          }}
                          />
                        </div>
                        {/* Render other lifestyle fields (stressLevel, alcohol, smoking) */}
                        {currentSection.fields
                          .filter(field => !['diet', 'exercise', 'sleep'].includes(field.name))
                          .map((field) => {
                            const fieldError = errors[field.name]?.message
                            const fieldLabel = t(`examination.fields.${field.name}`) || field.label
                            const translatedOptions = field.options ? field.options.map(opt => {
                              if (field.name === 'gender') {
                                const genderKey = opt.toLowerCase()
                                return t(`examination.genders.${genderKey}`) || opt
                              }
                              if (field.name === 'stressLevel') {
                                const stressKey = opt.toLowerCase().replace(/\s+/g, '')
                                return t(`examination.stressLevels.${stressKey}`) || opt
                              }
                              if (field.name === 'alcohol') {
                                const alcoholKey = opt.toLowerCase()
                                return t(`examination.alcoholOptions.${alcoholKey}`) || opt
                              }
                              if (field.name === 'smoking') {
                                const smokingKey = opt.toLowerCase().replace(/-/g, '')
                                return t(`examination.smokingOptions.${smokingKey}`) || opt
                              }
                              return opt
                            }) : null
                            if (field.type === 'select') {
                              return (
                                <Select
                                  key={field.name}
                                  label={fieldLabel}
                                  required={field.required}
                                  options={translatedOptions || field.options || []}
                                  error={fieldError}
                                  {...methods.register(field.name)}
                                />
                              )
                            }
                            return null
                          })}
                      </>
                    ) : (
                      currentSection.fields.map((field) => {
                        const fieldError = errors[field.name]?.message

                      const fieldLabel = t(`examination.fields.${field.name}`) || field.label
                      const translatedOptions = field.options ? field.options.map(opt => {
                        // Translate gender options
                        if (field.name === 'gender') {
                          const genderKey = opt.toLowerCase()
                          return t(`examination.genders.${genderKey}`) || opt
                        }
                        // Translate stress level options
                        if (field.name === 'stressLevel') {
                          const stressKey = opt.toLowerCase().replace(/\s+/g, '')
                          return t(`examination.stressLevels.${stressKey}`) || opt
                        }
                        // Translate alcohol options
                        if (field.name === 'alcohol') {
                          const alcoholKey = opt.toLowerCase()
                          return t(`examination.alcoholOptions.${alcoholKey}`) || opt
                        }
                        // Translate smoking options
                        if (field.name === 'smoking') {
                          const smokingKey = opt.toLowerCase().replace(/-/g, '')
                          return t(`examination.smokingOptions.${smokingKey}`) || opt
                        }
                        return opt
                      }) : null

                      if (field.type === 'textarea') {
                        return (
                          <div key={field.name} className="md:col-span-2">
                            <Textarea
                              label={fieldLabel}
                              required={field.required}
                              error={fieldError}
                              placeholder={field.placeholder}
                              {...methods.register(field.name)}
                            />
                          </div>
                        )
                      }

                      if (field.type === 'select') {
                        return (
                          <Select
                            key={field.name}
                            label={fieldLabel}
                            required={field.required}
                            options={translatedOptions || field.options || []}
                            error={fieldError}
                            {...methods.register(field.name)}
                          />
                        )
                      }

                      if (field.type === 'checkbox') {
                        return (
                          <div key={field.name} className="md:col-span-2">
                            <label className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 cursor-pointer hover:border-primary-300 dark:hover:border-primary-600 transition-colors">
                              <input
                                type="checkbox"
                                {...methods.register(field.name)}
                                className="mt-1 w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <div className="flex-1">
                                <span className="text-sm font-semibold text-gray-900 dark:text-white block">
                                  {fieldLabel}
                                  {field.required && <span className="text-red-500 ml-1">*</span>}
                                </span>
                                {field.name === 'clientConsent' && (
                                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                    {t('examination.consentText')}
                                  </p>
                                )}
                                {fieldError && (
                                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">{fieldError}</p>
                                )}
                              </div>
                            </label>
                          </div>
                        )
                      }

                      return (
                        <Input
                          key={field.name}
                          type={field.type}
                          label={fieldLabel}
                          placeholder={field.placeholder}
                          required={field.required}
                          error={fieldError}
                          readOnly={field.readOnly}
                          step={field.step}
                          {...methods.register(field.name)}
                        />
                      )
                    })
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Questionnaire Template Manager */}
                    <QuestionnaireTemplateManager
                      customQuestions={customQuestions}
                      onLoadTemplate={(templateQuestions) => {
                        if (templateQuestions && Array.isArray(templateQuestions)) {
                          setCustomQuestions(templateQuestions.length > 0 ? templateQuestions : [createQuestion()])
                          hasUserInputRef.current = true
                        }
                      }}
                      onSaveTemplate={() => {
                        // Template saved successfully
                      }}
                    />
                    
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Create bespoke questions to capture unique details for this case. Choose
                        between a structured response (Yes / No / Maybe / Not Known) or an open-ended
                        text area (max 1000 characters).
                      </p>
                    </div>
                    <div className="space-y-4">
                      {customQuestions.map((question, index) => (
                        <div key={question.id} className="p-4 rounded-2xl border border-gray-100 bg-white/70 shadow-sm space-y-3">
                          <div className="flex justify-between items-center">
                            <p className="text-sm font-semibold text-gray-700">
                              Question {index + 1}
                            </p>
                            <button
                              type="button"
                              onClick={() => removeQuestion(question.id)}
                              className="text-red-500 hover:text-red-600"
                              disabled={customQuestions.length === 1}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                          <Input
                            label={t('examination.questionPrompt')}
                            placeholder={t('examination.questionPromptPlaceholder')}
                            value={question.prompt}
                            onChange={(e) => updateQuestion(question.id, 'prompt', e.target.value)}
                          />
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                              {t('examination.responseType') || 'Response Type'}
                            </label>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => updateQuestion(question.id, 'type', 'choice')}
                                className={`flex-1 px-4 py-2.5 rounded-xl border-2 transition-all ${
                                  question.type === 'choice'
                                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-semibold'
                                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-primary-300 dark:hover:border-primary-600'
                                }`}
                              >
                                {t('examination.questionTypes.choice')}
                              </button>
                              <button
                                type="button"
                                onClick={() => updateQuestion(question.id, 'type', 'text')}
                                className={`flex-1 px-4 py-2.5 rounded-xl border-2 transition-all ${
                                  question.type === 'text'
                                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-semibold'
                                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-primary-300 dark:hover:border-primary-600'
                                }`}
                              >
                                {t('examination.questionTypes.text')}
                              </button>
                            </div>
                          </div>

                          {question.type === 'choice' ? (
                            <Select
                              label={t('examination.answer')}
                              options={CHOICE_OPTIONS.map(opt => ({
                                value: opt.toLowerCase().replace(' ', ''),
                                label: t(`examination.choiceOptions.${opt.toLowerCase().replace(' ', '')}`) || opt
                              }))}
                              value={question.answer}
                              onChange={(e) => updateQuestion(question.id, 'answer', e.target.value)}
                            />
                          ) : (
                            <div>
                              <Textarea
                                label={t('examination.response')}
                                placeholder={t('examination.responsePlaceholder')}
                                value={question.answer}
                                maxLength={1000}
                                onChange={(e) => updateQuestion(question.id, 'answer', e.target.value)}
                              />
                              <p className="text-xs text-gray-400 mt-1 text-right">
                                {question.answer.length}/1000
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <Button type="button" variant="secondary" className="w-full flex items-center justify-center gap-2 whitespace-nowrap" onClick={addQuestion}>
                      <Plus size={18} />
                      Add Question
                    </Button>
                  </div>
                )}
              </Card>
            </motion.div>
          </AnimatePresence>

          <div className="flex flex-col sm:flex-row justify-between gap-3">
            <div>
              {currentStep > 0 && (
                <Button type="button" variant="secondary" onClick={goBack} className="flex items-center gap-2">
                  <ChevronLeft size={18} />
                  {t('common.buttons.back')}
                </Button>
              )}
            </div>
            <div className="flex gap-3 justify-end flex-wrap">
              {currentStep < totalSteps - 1 && (
                <Button type="button" onClick={goNext} className="flex items-center gap-2">
                  {t('common.buttons.next')}
                  <ChevronRight size={18} />
                </Button>
              )}
              {currentStep === totalSteps - 1 && (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    className="flex items-center gap-2"
                    onClick={() => {
                      setPdfEditorBlank(false)
                      setPdfEditorOpen(true)
                    }}
                  >
                    <Download size={18} />
                    Print examination
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    className="flex items-center gap-2"
                    onClick={() => {
                      setPdfEditorBlank(true)
                      setPdfEditorOpen(true)
                    }}
                  >
                    <FileText size={18} />
                    Blank paper chart
                  </Button>
                  <Button type="submit" disabled={submitting} className="flex items-center gap-2">
                    {submitting ? t('examination.saving') : t('examination.saveExamination')}
                    {!submitting && <CheckCircle2 size={18} />}
                  </Button>
                </>
              )}
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}

export default Examination

